import { cmgSectorScheme } from '@cmg/common';
import isNil from 'lodash/isNil';
import { v4 as uuidV4 } from 'uuid';

import {
  AdvancedFilterRule,
  AdvancedFilterValue,
} from '../../../common/components/form/advanced-filter/advanced-filter.model';
import { objectCamelToSnake } from '../../../common/helpers/url-helpers';
import { DatalabScreens } from '../constants';
import {
  DatalabFilterValues,
  DatalabFormValues,
  DatalabRequest,
  DatalabRequestDto,
  DatalabRequestDtoFilter,
  FilterSettings,
} from '../types';
import { getSummaryColumns, isInTable } from './datalab.model';
import {
  getInternationalOfferingTypes,
  getOfferingTypeConfig,
  getOfferingTypeWithoutInternalTypes,
} from './filters.model';

/**
 * Helper function to convert a string or number to a number type or undefined
 * @param value The value to transform
 * @returns A number or undefined if the value cannot be converted to a valid number
 */
export function toNumberOrUndefined(value: string | number | null | undefined): number | undefined {
  if (value == null || value === '') {
    return undefined;
  }

  const num = Number(value);
  return isNaN(num) ? undefined : num;
}

/**
 * Transforms the advanced query fields to ensure they have a unique ID.
 * This is useful for ensuring that each rule can be uniquely identified in the UI.
 *
 * @param advancedQuery The advanced query object to transform
 * @returns The transformed advanced query object with unique IDs for each rule
 */
export function transformAdvancedQueryFields(
  advancedQuery: AdvancedFilterValue | undefined
): AdvancedFilterValue | undefined {
  if (!advancedQuery || !advancedQuery.rules || !Array.isArray(advancedQuery.rules)) {
    return advancedQuery;
  }

  return {
    ...advancedQuery,
    rules: advancedQuery.rules.map((rule: AdvancedFilterRule) => {
      if (!rule.field) {
        return rule;
      }

      return {
        ...rule,
        id: uuidV4(),
      };
    }),
  };
}

/**
 * TODO: DatalabRequest and DatalabRequestDto are obfuscating a difference between a table request payload
 * and a download request payload. Two separate types should be created here along with two separate mapping
 * functions. e.g. the download will never have pagination and the table will never have include_* fields.
 */
export function createDatalabRequestDto(query: DatalabRequest): DatalabRequestDto {
  return {
    data_lab: {
      start_date_value: query.datalab.startDateValue,
      end_date_value: query.datalab.endDateValue,
      offering_type_values: query.datalab.offeringTypeValues,
      international_offering_type_values: query.datalab.internationalOfferingTypeValues,
      by_sectors_values: query.datalab.bySectorsValues,
      by_custom_sectors_values: query.datalab.byCustomSectorsValues,
      custom_filter: query.datalab.customFilter,
      market_cap_value_min: query.datalab.marketCapValueMin,
      market_cap_value_max: query.datalab.marketCapValueMax,
      selling_shareholder_pct_min: query.datalab.sellingShareholderPctMin,
      selling_shareholder_pct_max: query.datalab.sellingShareholderPctMax,
      offering_size_value_min: query.datalab.offeringSizeValueMin,
      offering_size_value_max: query.datalab.offeringSizeValueMax,
      underwriters_values: query.datalab.underwritersValues,
      filter_for_allocations: query.datalab.filterForAllocations,
      funds_values: query.datalab.fundsValues,
      sponsors_values: query.datalab.sponsorsValues,
      advisories_values: query.datalab.advisoriesValues,
      spac: query.datalab.spac,
      advanced_query: objectCamelToSnake(query.datalab.advancedQuery),
      left_lead_only: query.datalab.leftLeadOnly,
      avg_method: query.datalab.avgMethod,
      pl_credit: query.datalab.plCredit,
      countries_values: query.datalab.countries,
    },
    summary_columns: query.summaryColumns,
    page: query.page,
    per_page: query.perPage,
    order_field: query.orderField,
    order_direction: query.orderDirection,
    columns: query.columns,
    include_advisors: query.includeAdvisors,
    include_offering_notes: query.includeOfferingNotes,
    include_ioi_notes: query.includeIoiNotes,
    include_underwriters: query.includeUnderwriters,
    include_sponsors: query.includeSponsors,
    include_fund_ioi: query.includeFundIoi,
    use_custom_sectors: query.useCustomSectors,
  };
}

export function findSectorWithTitle(title: string) {
  const sectorsObjects = Object.values(cmgSectorScheme);
  const subSectorObjects = sectorsObjects.flatMap(sector => sector.children);

  return [...sectorsObjects, ...subSectorObjects].find(sector => sector.title === title)?.value;
}

/**
 * Maps data from DatalabRequestDto.data_lab format (snake_case) directly to DatalabFormValues format (camelCase)
 * for use in Formik forms. This is the reverse transformation of createDatalabRequestDto.
 *
 * @param requestFilters The filters from the data_lab request DTO
 * @param existingFormValues Optional existing form values to use as fallbacks
 * @returns A DatalabFormValues object ready to use with Formik
 */
export function mapFromDatalabRequestDtoFilters(
  requestFilters: DatalabRequestDtoFilter,
  existingFormValues: DatalabFormValues,
  initialValues: DatalabFormValues
): DatalabFormValues {
  return {
    groupId: existingFormValues.groupId,
    date:
      !isNil(requestFilters.start_date_value) && !isNil(requestFilters.end_date_value)
        ? {
            start: requestFilters.start_date_value,
            end: requestFilters.end_date_value,
            type: 'CUSTOM',
          }
        : initialValues.date,
    offeringTypeInternal: requestFilters.offering_type ?? initialValues.offeringTypeInternal,
    offeringType: requestFilters.offering_type ?? initialValues.offeringTypeInternal,
    marketCap:
      isNil(requestFilters.market_cap_value_min) && isNil(requestFilters.market_cap_value_max)
        ? initialValues.marketCap
        : {
            min: toNumberOrUndefined(requestFilters.market_cap_value_min),
            max: toNumberOrUndefined(requestFilters.market_cap_value_max),
          },

    offeringSize:
      isNil(requestFilters.offering_size_value_min) && isNil(requestFilters.offering_size_value_max)
        ? initialValues.offeringSize
        : {
            min: toNumberOrUndefined(requestFilters.offering_size_value_min),
            max: toNumberOrUndefined(requestFilters.offering_size_value_max),
          },

    sellingShareholderPct:
      isNil(requestFilters.selling_shareholder_pct_min) &&
      isNil(requestFilters.selling_shareholder_pct_max)
        ? initialValues.sellingShareholderPct
        : {
            min: toNumberOrUndefined(requestFilters.selling_shareholder_pct_min),
            max: toNumberOrUndefined(requestFilters.selling_shareholder_pct_max),
          },
    sector: !isNil(requestFilters.by_sectors_values)
      ? requestFilters.by_sectors_values.map(sector => findSectorWithTitle(sector) || '')
      : initialValues.sector,
    customSector: requestFilters.by_custom_sectors_values ?? initialValues.customSector,
    underwriters: requestFilters.underwriters_values ?? initialValues.underwriters,
    sponsors: requestFilters.sponsors_values ?? initialValues.sponsors,
    advisories: requestFilters.advisories_values ?? initialValues.advisories,
    funds: requestFilters.funds_values ?? initialValues.funds,
    spac: requestFilters.spac ?? initialValues.spac,
    advancedQuery: !isNil(requestFilters.advanced_query)
      ? transformAdvancedQueryFields(requestFilters.advanced_query)
      : initialValues.advancedQuery,
    leftLeadOnly: requestFilters.left_lead_only ?? initialValues.leftLeadOnly,
    ioiType: initialValues.ioiType,
    countries: requestFilters.countries_values ?? initialValues.countries,
    useCustomSectors: initialValues.useCustomSectors,
  };
}

/**
 * Takes filters set by the user and adds some calculated fields on the way out to the api.
 */
export function decorateDatalabFilterValues(
  section: string,
  filters: DatalabFilterValues,
  filterSettings: FilterSettings
): DatalabFilterValues {
  const screen = DatalabScreens[section] ?? (section as DatalabScreens);

  let customFilter;
  if (section === DatalabScreens.LOCK_UP_EXPIRATION) {
    customFilter = 'future_expiration_date';
  } else if (section === DatalabScreens.IPO_BACKLOG) {
    customFilter = 'filed_or_live';
  }

  const { shouldFilterForAllocations } = filterSettings;

  // We will only pull defaults if by default nothing is selected
  const offeringTypeValues =
    (filters.offeringTypeInternal ?? []).length === 0
      ? getOfferingTypeConfig(screen).defaultSelectedValues
      : filters.offeringTypeValues;

  const { ioiType, useCustomSectors, ...restFilters } = filters;
  const filterForAllocations = shouldFilterForAllocations ? ioiType : false;
  const offeringTypes = getOfferingTypeWithoutInternalTypes(offeringTypeValues);

  return {
    ...restFilters,
    filterForAllocations,
    customFilter,
    internationalOfferingTypeValues: getInternationalOfferingTypes(filters.offeringTypeInternal),
    offeringTypeValues: offeringTypes,
  };
}

/**
 * Get's a payload tailored for fetching a datalab table.
 */
export function getPayload(
  section,
  sectionType,
  filters: DatalabFilterValues,
  filterSettings: FilterSettings,
  page,
  perPage,
  orderField,
  orderDirection
): DatalabRequest {
  let payload: any = {
    datalab: decorateDatalabFilterValues(section, filters, filterSettings),
    summaryColumns: getSummaryColumns(section, sectionType),
    useCustomSectors: filters.useCustomSectors,
  };

  if (isInTable(section, sectionType)) {
    payload = {
      ...payload,
      page: page,
      perPage: perPage,
      orderField: orderField,
      orderDirection: orderDirection,
    };
  }

  return payload;
}
